import createCommunityPost from '@dehaat/kisan-app-bl/api/createCommunityPost';
import { DEFAULT_LANG_NAME } from '@dehaat/kisan-app-bl/constants/common';
import { GetServerSideProps } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { ChangeEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import SuccessPopup from '@/components/askQuestion/SuccessPopup';
import VoiceRecorder from '@/components/askQuestion/VoiceRecorder';
import ErrorModal from '@/components/ErrorModal';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import Close from '@/components/icons/Close';
import DeleteIcon from '@/components/icons/Delete';
import MicIcon from '@/components/icons/Mic';
import PlusIcon from '@/components/icons/PlusIcon';
import LoginScreen from '@/components/login/LoginScreen';
import Okay from '@/components/modalActions/Okay';
import Paragraph from '@/components/Paragraph';
import Spinner from '@/components/Spinner';
import SubHeading, { VARIANT as HEADING_VARIANT } from '@/components/SubHeading';
import { AuthContext } from '@/context/AuthProvider';
import useErrorModalState from '@/hooks/useErrorModalState';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import useVoiceRecorder from '@/hooks/useVoiceRecorder';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
const AskAgriQuestionPage = () => {
  const {
    isAuthenticated
  } = useContext(AuthContext);
  const {
    errorModalState,
    hideErrorModal,
    setErrorModalState
  } = useErrorModalState();
  const diseaseImageUrls = useRef<string[]>([]);
  const diseaseInputRef = useRef<HTMLInputElement>(null);
  const questionInputRef = useRef<HTMLTextAreaElement>(null);
  const {
    locale
  } = useRouter();
  const [audioUrl, setAudioUrl] = useState('');
  const [diseaseImages, setDiseaseImages] = useState<Blob[]>([]);
  const [isPosting, setIsPosting] = useState(false);
  const [postSlug, setPostSlug] = useState('');
  const [question, setQuestion] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const {
    t
  } = useTranslation('askQuestion');
  const {
    audio,
    isRecording,
    isVoiceRecordingSupported,
    toggleRecording
  } = useVoiceRecorder();
  const isAudioUploadAllowed = isVoiceRecordingSupported && diseaseImages.length == 0;
  const isPhotoUploadAllowed = !audioUrl;
  const isPostingAllowed = !!audioUrl || diseaseImageUrls.current.length > 0 || !!question;
  const onSubmit = async () => {
    setIsPosting(true);
    const audios = [];
    if (audio && audioUrl) {
      audios.push(audio);
    }
    const communityPost = await createCommunityPost(locale as keyof typeof DEFAULT_LANG_NAME, question, audios, diseaseImages);
    if (communityPost) {
      setIsPosting(false);
      setPostSlug(communityPost.slug);
    } else {
      setIsPosting(false);
      setErrorModalState({
        ...errorModalState,
        show: true
      });
    }
    trackCustomEvent(CustomEventTypes.ASK_HELP_QUESTION_POSTED, JSON.stringify({
      Type: 'Agri',
      Question: question
    }));
  };
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length === 1) {
      const file = e.target.files[0];
      setDiseaseImages(prev => [...prev, file]);
      diseaseImageUrls.current.push(URL.createObjectURL(file));
    }
  };
  const addImage = () => {
    if (diseaseInputRef.current) {
      diseaseInputRef.current.value = '';
      diseaseInputRef.current.click();
    }
  };
  const removeImage = useCallback((index: number) => {
    setDiseaseImages(prev => {
      const newImages = [...prev];
      newImages.splice(index, 1);
      return newImages;
    });
    URL.revokeObjectURL(diseaseImageUrls.current[index]);
    diseaseImageUrls.current.splice(index, 1);
  }, []);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const toggleLoginModal = useCallback(() => setShowLogin(prev => !prev), []);
  useEffect(() => {
    if (audio) {
      setAudioUrl(prevAudioUrl => {
        URL.revokeObjectURL(prevAudioUrl);
        return URL.createObjectURL(audio);
      });
    }
  }, [audio]);
  useEffect(() => {
    trackCustomEvent(CustomEventTypes.ASK_HELP_USER_LANDED_ON_AGRI_QUESTION_PAGE);
    questionInputRef.current?.focus();
    questionInputRef.current?.scrollIntoView();
  }, []);
  return <main className="bg-neutral-10 min-h-screen" data-sentry-component="AskAgriQuestionPage" data-sentry-source-file="ask-agri-question.tsx">
      <HeaderWithBackButton heading={t('ask_question')} data-sentry-element="HeaderWithBackButton" data-sentry-source-file="ask-agri-question.tsx" />
      <section className="bg-white min-h-screen m-auto max-w-7xl relative px-4 py-16 w-full">
        <SubHeading className="mt-4" variant={HEADING_VARIANT.BIG} data-sentry-element="SubHeading" data-sentry-source-file="ask-agri-question.tsx">
          {t('ask_question_here')}
        </SubHeading>
        {audioUrl && <div className="flex gap-4 items-center mt-3">
            <audio className="grow" controls controlsList="nodownload" src={audioUrl} />
            <button aria-label="Delete audio message" className="mr-1" onClick={() => {
          URL.revokeObjectURL(audioUrl);
          setAudioUrl('');
        }}>
              <DeleteIcon className="fill-transparent grow-0 h-8 shrink-0 stroke-2 stroke-primary-100 w-8" />
            </button>
          </div>}
        <div className="flex gap-4 mt-3">
          <textarea className="border grow h-36 p-4 rounded-lg shadow-md" onChange={e => setQuestion(e.target.value)} placeholder={t('write_problem')} ref={questionInputRef} value={question} />
          <button className={`grow-0 h-10 mt-10 p-2 rounded-full shrink-0 w-10 ${isAudioUploadAllowed ? 'bg-primary-100' : 'bg-neutral-20'}`} disabled={!isAudioUploadAllowed} onClick={toggleRecording}>
            <MicIcon className="fill-transparent stroke-white" data-sentry-element="MicIcon" data-sentry-source-file="ask-agri-question.tsx" />
          </button>
          {isVoiceRecordingSupported && isRecording && <VoiceRecorder toggleRecording={toggleRecording} />}
        </div>
        <input accept=".jpg, .jpeg, .png, .webp" className="hidden" data-testid="ask-agri-question-disease-upload-input" onChange={onChange} ref={diseaseInputRef} type="file" />
        <section className="flex flex-wrap gap-4 mt-8">
          <button className={`border-2 border-dashed grow-0 h-20 p-2 rounded-lg shrink-0 w-20 ${isPhotoUploadAllowed ? 'bg-primary-20 border-primary-100' : 'bg-neutral-20 border-neutral-50'}`} data-testid="ask-agri-question-disease-upload-button" disabled={!isPhotoUploadAllowed} onClick={addImage}>
            <PlusIcon className={`fill-transparent m-auto ${isPhotoUploadAllowed ? 'stroke-primary-100' : 'stroke-neutral-50'}`} height={24} width={24} data-sentry-element="PlusIcon" data-sentry-source-file="ask-agri-question.tsx" />
            <Paragraph className={`mt-1 text-center whitespace-nowrap ${isPhotoUploadAllowed ? 'text-primary-100' : 'text-neutral-50'}`} isHighlighted data-sentry-element="Paragraph" data-sentry-source-file="ask-agri-question.tsx">
              {t('add_photo')}
            </Paragraph>
          </button>
          {diseaseImageUrls.current.map((url, index) => <div className="border grow-0 h-20 overflow-hidden relative rounded-lg shrink-0 w-20" key={url}>
              <Image alt="Disease photo" src={url} fill />
              <button aria-label="Remove photo" className="absolute bg-black/70 h-6 right-1 rounded-full top-1 w-6" onClick={() => removeImage(index)}>
                <Close className="h-4 m-auto stroke-2 stroke-white w-4" />
              </button>
            </div>)}
        </section>
        <button className={`absolute bottom-6 flex items-center justify-center left-4 p-4 right-4 rounded-lg text-white ${isPostingAllowed ? 'bg-primary-100' : 'bg-neutral-20'}`} data-testid="ask-agri-question-post" disabled={!isPostingAllowed} onClick={isAuthenticated ? onSubmit : toggleLoginModal}>
          {t('post_question')}
        </button>
        {isPosting && <Spinner />}
        <ErrorModal {...errorModalState} onClose={hideErrorModal} data-sentry-element="ErrorModal" data-sentry-source-file="ask-agri-question.tsx">
          <Okay handleClick={hideErrorModal} data-sentry-element="Okay" data-sentry-source-file="ask-agri-question.tsx" />
        </ErrorModal>
        {postSlug && <SuccessPopup postSlug={postSlug} readMoreHref="/crop/all" />}
        {showLogin && <LoginScreen isPopup onBack={toggleLoginModal} onLogin={toggleLoginModal} />}
      </section>
    </main>;
};
export const getServerSideProps: GetServerSideProps<{
  title: string;
}> = async () => ({
  props: {
    title: 'Ask Question'
  }
});
export default AskAgriQuestionPage;